import { CONSENT_PURPOSES, formatDate, privacyNoticeSchema } from '@erp/shared';
import { useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { Alert } from '../../components/ui/alert.tsx';
import { RequestsSection } from './privacy-requests.tsx';
import { Badge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Card } from '../../components/ui/card.tsx';
import { Dialog } from '../../components/ui/dialog.tsx';
import { Field, Input, Select } from '../../components/ui/field.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { api, errorMessage } from '../../lib/api.ts';
import {
  formatDay,
  privacyKeys,
  useCurrentNotice,
  useHolidays,
  useNoticeHistory,
  usePendingRevocations,
  type PrivacyNotice,
} from '../../lib/privacy-api.ts';
import { useCan, useSession } from '../../lib/session.ts';
import { useToast } from '../../lib/toast.tsx';

const textarea = 'w-full rounded-lg border border-border bg-surface p-3 text-base sm:text-sm';

/** Privacy: the notice (versions), revocations to complete and non-working days. */
export function PrivacyPage() {
  const canHolidays = useCan('settings.manage');
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Privacidad</h1>
        <p className="text-sm text-muted-foreground">
          Solicitudes ARCO-POL, aviso de privacidad, revocaciones de consentimiento y días que no
          cuentan para los plazos legales.
        </p>
      </div>
      <RequestsSection />
      <NoticeSection />
      <RevocationsSection />
      {canHolidays && <HolidaysSection />}
    </div>
  );
}

function NoticeView({ notice }: { notice: PrivacyNotice }) {
  const { data: session } = useSession();
  return (
    <div className="space-y-2 text-sm">
      <p className="text-muted-foreground">
        Versión {notice.version} · publicada el{' '}
        {formatDate(notice.publishedAt, session?.tenant.timezone)} por {notice.publishedBy}
      </p>
      {notice.kind === 'url' && notice.url ? (
        <a
          href={notice.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          {notice.url} <ExternalLink className="size-3.5" aria-hidden="true" />
        </a>
      ) : (
        <p className="max-h-40 overflow-y-auto whitespace-pre-line rounded-lg bg-muted p-3">
          {notice.body}
        </p>
      )}
      <p>
        <span className="font-medium">Solicitudes ARCO-POL: </span>
        {notice.arcoChannels}
      </p>
    </div>
  );
}

function NoticeSection() {
  const current = useCurrentNotice();
  const history = useNoticeHistory();
  const [publishing, setPublishing] = useState(false);
  return (
    <Card className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="font-semibold">Aviso de privacidad</h2>
        <Button
          variant="brand"
          onClick={() => {
            setPublishing(true);
          }}
        >
          Publicar {current.data ? 'nueva versión' : 'el aviso'}
        </Button>
      </div>
      {current.isPending ? (
        <Loading />
      ) : current.data ? (
        <NoticeView notice={current.data} />
      ) : (
        <Alert tone="warning" title="Todavía no hay aviso de privacidad">
          Sin aviso no se pueden registrar consentimientos: cada consentimiento guarda la versión
          que la persona aceptó.
        </Alert>
      )}
      {(history.data?.length ?? 0) > 1 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-primary">Versiones anteriores</summary>
          <ul className="mt-3 space-y-4">
            {history.data?.slice(1).map((n) => (
              <li key={n.id}>
                <NoticeView notice={n} />
              </li>
            ))}
          </ul>
        </details>
      )}
      {publishing && (
        <PublishDialog
          previous={current.data ?? null}
          onClose={() => {
            setPublishing(false);
          }}
        />
      )}
    </Card>
  );
}

function PublishDialog({
  previous,
  onClose,
}: {
  previous: PrivacyNotice | null;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const notify = useToast();
  const [kind, setKind] = useState<'text' | 'url'>(previous?.kind ?? 'url');
  const [body, setBody] = useState(previous?.body ?? '');
  const [url, setUrl] = useState(previous?.url ?? '');
  const [arcoChannels, setArcoChannels] = useState(previous?.arcoChannels ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const publish = async () => {
    const parsed = privacyNoticeSchema.safeParse({
      kind,
      arcoChannels,
      ...(kind === 'text' ? { body } : { url }),
    });
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])));
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      const notice = await api<PrivacyNotice>('/privacy/notices', {
        method: 'POST',
        body: parsed.data,
      });
      await client.invalidateQueries({ queryKey: ['privacy'] });
      notify(`Aviso de privacidad publicado (versión ${String(notice.version)}).`, 'success');
      onClose();
    } catch (error) {
      notify(errorMessage(error), 'error');
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      title="Publicar aviso de privacidad"
      description="Cada publicación es una versión nueva: las anteriores no se editan, porque los consentimientos registrados citan la versión aceptada."
      onClose={onClose}
      className="sm:w-[min(40rem,calc(100vw-2rem))]"
    >
      <div className="space-y-4">
        <fieldset className="flex gap-5">
          <legend className="mb-2 text-sm font-medium">El aviso es</legend>
          {(['url', 'text'] as const).map((k) => (
            <label key={k} className="flex min-h-11 items-center gap-2 text-sm">
              <input
                type="radio"
                className="size-5"
                checked={kind === k}
                onChange={() => {
                  setKind(k);
                }}
              />
              {k === 'url' ? 'Un enlace (tu sitio web)' : 'Un texto'}
            </label>
          ))}
        </fieldset>
        {kind === 'url' ? (
          <Field label="Enlace del aviso" error={errors.url}>
            {(a11y) => (
              <Input
                {...a11y}
                type="url"
                placeholder="https://"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                }}
              />
            )}
          </Field>
        ) : (
          <Field label="Texto del aviso" error={errors.body}>
            {(a11y) => (
              <textarea
                {...a11y}
                rows={8}
                className={textarea}
                value={body}
                onChange={(e) => {
                  setBody(e.target.value);
                }}
              />
            )}
          </Field>
        )}
        <Field
          label="Cómo presentar solicitudes ARCO-POL"
          hint="Obligatorio por ley: correo, dirección o formulario donde se reciben las solicitudes de acceso, rectificación, cancelación, oposición, portabilidad, olvido y limitación."
          error={errors.arcoChannels}
        >
          {(a11y) => (
            <textarea
              {...a11y}
              rows={3}
              className={textarea}
              value={arcoChannels}
              onChange={(e) => {
                setArcoChannels(e.target.value);
              }}
            />
          )}
        </Field>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="brand" loading={busy} onClick={() => void publish()}>
            Publicar versión
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function RevocationsSection() {
  const pending = usePendingRevocations();
  const client = useQueryClient();
  const notify = useToast();
  const complete = async (consentId: string) => {
    try {
      await api(`/privacy/revocations/${consentId}/complete`, { method: 'POST' });
      await client.invalidateQueries({ queryKey: privacyKeys.revocations });
      notify('Revocación completada.', 'success');
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };
  return (
    <Card className="space-y-3">
      <div>
        <h2 className="font-semibold">Revocaciones por completar</h2>
        <p className="text-sm text-muted-foreground">
          En el sistema la revocación ya surtió efecto. Aquí queda lo que hay que hacer fuera
          (avisar a una inmobiliaria colaboradora, quitar de una lista de envíos) dentro de 5 días
          hábiles.
        </p>
      </div>
      {pending.isPending ? (
        <Loading />
      ) : pending.error ? (
        <Alert tone="danger">{errorMessage(pending.error)}</Alert>
      ) : pending.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">No hay revocaciones pendientes.</p>
      ) : (
        <ul className="divide-y divide-border">
          {pending.data.map((r) => (
            <li key={r.consentId} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1">
                <Link
                  to={`/contactos/${r.contact.id}`}
                  className="font-medium text-primary hover:underline"
                >
                  {r.contact.name}
                </Link>
                <p className="text-sm text-muted-foreground">
                  {CONSENT_PURPOSES[r.purpose].label} · vence el {formatDay(r.dueOn)}{' '}
                  {r.overdue && <Badge tone="danger">Vencida</Badge>}
                </p>
              </div>
              <Button size="sm" variant="secondary" onClick={() => void complete(r.consentId)}>
                Marcar como completada
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function HolidaysSection() {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const holidays = useHolidays(year);
  const client = useQueryClient();
  const notify = useToast();
  const [date, setDate] = useState('');
  const [name, setName] = useState('');
  const refresh = () => client.invalidateQueries({ queryKey: ['holidays'] });
  const add = async () => {
    try {
      await api('/settings/holidays', { method: 'POST', body: { date, name } });
      setDate('');
      setName('');
      await refresh();
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };
  const remove = async (id: string) => {
    try {
      await api(`/settings/holidays/${id}`, { method: 'DELETE' });
      await refresh();
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };
  return (
    <Card className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-semibold">Días no hábiles</h2>
          <p className="text-sm text-muted-foreground">
            Además de fines de semana y feriados nacionales: fiestas locales o cierres de la
            oficina.
          </p>
        </div>
        <Select
          aria-label="Año"
          className="sm:w-32"
          value={year}
          onChange={(e) => {
            setYear(Number(e.target.value));
          }}
        >
          {[year - 1, year, year + 1].map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Select>
      </div>
      {holidays.data && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <h3 className="mb-2 text-sm font-medium">Feriados nacionales</h3>
            <ul className="space-y-1 text-sm text-muted-foreground">
              {holidays.data.national.map((h) => (
                <li key={h.date}>
                  {formatDay(h.date)} · {h.name}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="mb-2 text-sm font-medium">De tu inmobiliaria</h3>
            {holidays.data.own.length === 0 && (
              <p className="text-sm text-muted-foreground">Ninguno.</p>
            )}
            <ul className="space-y-1 text-sm">
              {holidays.data.own.map((h) => (
                <li key={h.id} className="flex items-center justify-between gap-2">
                  <span>
                    {formatDay(h.date)} · {h.name}
                  </span>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={`Quitar ${h.name}`}
                    onClick={() => void remove(h.id)}
                  >
                    <Trash2 />
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      <form
        className="flex flex-col gap-2 sm:flex-row sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          void add();
        }}
      >
        <Field label="Fecha">
          {(a11y) => (
            <Input
              {...a11y}
              type="date"
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
              }}
            />
          )}
        </Field>
        <div className="flex-1">
          <Field label="Motivo">
            {(a11y) => (
              <Input
                {...a11y}
                placeholder="Fiestas patronales"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
              />
            )}
          </Field>
        </div>
        <Button type="submit" variant="secondary" disabled={!date || !name.trim()}>
          Agregar
        </Button>
      </form>
    </Card>
  );
}
